// ─────────────────────────────────────────────────────────────────────────────
// badges.js — small presentational atoms shared across the Nodes page
//   StatusBadge · TypeBadge · TrustScore · LoadValue · AlertBadge
//   plus the status/type color config consumed by the table + map.
// ─────────────────────────────────────────────────────────────────────────────
import styles from "./badges.module.css";
import { DASH } from "../format";

// ── Status → colors ──────────────────────────────────────────────────────────
export const STATUS_CONFIG = {
  Optimal:  { color: "#00e87a", bg: "rgba(0,232,122,0.12)",  border: "rgba(0,232,122,0.30)" },
  Warning:  { color: "#ffc542", bg: "rgba(255,197,66,0.14)", border: "rgba(255,197,66,0.35)" },
  Critical: { color: "#ff4560", bg: "rgba(255,69,96,0.12)",  border: "rgba(255,69,96,0.30)"  },
  Offline:  { color: "#6a9080", bg: "rgba(106,144,128,0.12)",border: "rgba(106,144,128,0.25)" },
  Online:   { color: "#00e87a", bg: "rgba(0,232,122,0.12)",  border: "rgba(0,232,122,0.30)" },
};

export const TYPE_CONFIG = {
  relay: { color: "#00c8e8", bg: "rgba(0,200,232,0.1)",  border: "rgba(0,200,232,0.25)" },
  exit:  { color: "#a855f7", bg: "rgba(168,85,247,0.1)", border: "rgba(168,85,247,0.25)" },
};

/** Resolves the accent color for a status label (used for node-id text, map dots). */
export function statusColor(status) {
  return (STATUS_CONFIG[status] || STATUS_CONFIG.Offline).color;
}

// ── Status pill ──────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Offline;
  return (
    <span className={styles.badge} style={{
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      <span className={styles.dot} style={{ background: cfg.color, boxShadow: `0 0 5px ${cfg.color}` }} />
      {status}
    </span>
  );
}

// ── Type pill (Relay / Exit) ─────────────────────────────────────────────────
export function TypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.relay;
  return (
    <span className={styles.badge} style={{
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      {type === "exit" ? "Exit" : "Relay"}
    </span>
  );
}

// ── Trust score (colored number) ─────────────────────────────────────────────
export function TrustScore({ value }) {
  if (value === null || value === undefined) return <span className={styles.muted}>{DASH}</span>;
  const color = value >= 90 ? "#00e87a" : value >= 70 ? "#ffc542" : "#ff4560";
  return <span className={styles.trust} style={{ color }}>{value}</span>;
}

// ── Load (percentage text; reddens under pressure) ───────────────────────────
export function LoadValue({ value }) {
  if (value === null || value === undefined) return <span className={styles.muted}>{DASH}</span>;
  const color = value > 80 ? "#ff4560" : value > 60 ? "#ffc542" : "var(--text-primary)";
  return <span className={styles.load} style={{ color }}>{Math.round(value)}%</span>;
}

// ── Alerts (red bubble or dash) ──────────────────────────────────────────────
// Zero alerts shows a dash by design — the absence of a badge IS the reading, so
// this is a deliberate exception to the page's "0 is a real value" rule.
export function AlertBadge({ count }) {
  if (!count) return <span className={styles.muted}>{DASH}</span>;
  return <span className={styles.alert}>{count}</span>;
}
